import { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardHeader, Btn, Select, Table, fmt, Badge, PageHeader } from '../components/ui';
import { RefreshCw } from 'lucide-react';

export default function TransactionsPage() {
  const [txns, setTxns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [filters, setFilters] = useState({ customer_id: '', script_id: '', transaction_type: '', from_date: '', to_date: '' });

  useEffect(() => {
    api.get('/masters/customers').then(r => setCustomers(r.data));
    api.get('/masters/scripts').then(r => setScripts(r.data));
    load();
  }, []);

  const load = () => api.get('/transactions', { params: filters }).then(r => setTxns(r.data));

  const setF = k => e => setFilters(f => ({ ...f, [k]: e.target.value }));

  const totalBuy = txns.filter(t => t.transaction_type === 'BUY').reduce((s, t) => s + parseFloat(t.net_amount || 0), 0);
  const totalSell = txns.filter(t => t.transaction_type === 'SELL').reduce((s, t) => s + parseFloat(t.net_amount || 0), 0);
  const totalPnL = txns.filter(t => t.transaction_type === 'SELL').reduce((s, t) => s + parseFloat(t.profit_loss || 0), 0);

  return (
    <div>
      <PageHeader title="All Transactions" subtitle="Buy & Sell history with filters" />

      {/* Filters */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ padding: '16px 20px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Select label="Type" value={filters.transaction_type} onChange={setF('transaction_type')} containerStyle={{ minWidth: 130 }}>
            <option value="">All</option>
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
          </Select>
          <Select label="Customer" value={filters.customer_id} onChange={setF('customer_id')} containerStyle={{ minWidth: 200 }}>
            <option value="">All Customers</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.customer_code} — {c.customer_name}</option>)}
          </Select>
          <Select label="Script" value={filters.script_id} onChange={setF('script_id')} containerStyle={{ minWidth: 160 }}>
            <option value="">All Scripts</option>
            {scripts.map(s => <option key={s.id} value={s.id}>{s.script_name}</option>)}
          </Select>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>From Date</label>
            <input type="date" value={filters.from_date} onChange={setF('from_date')} style={{ padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>To Date</label>
            <input type="date" value={filters.to_date} onChange={setF('to_date')} style={{ padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none' }} />
          </div>
          <Btn onClick={load}><RefreshCw size={14} /> Apply</Btn>
          <Btn variant="ghost" onClick={() => { setFilters({ customer_id: '', script_id: '', transaction_type: '', from_date: '', to_date: '' }); load(); }}>Reset</Btn>
        </div>

        {/* Summary row */}
        <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 32 }}>
          <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Records: </span><strong>{txns.length}</strong></div>
          <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Total Buy: </span><strong style={{ color: '#1d4ed8' }}>{fmt(totalBuy)}</strong></div>
          <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Total Sell: </span><strong style={{ color: '#dc2626' }}>{fmt(totalSell)}</strong></div>
          <div style={{ fontSize: 13 }}><span style={{ color: '#64748b' }}>Net P&L: </span><strong style={{ color: totalPnL >= 0 ? '#059669' : '#dc2626' }}>{fmt(totalPnL)}</strong></div>
        </div>
      </Card>

      <Card>
        <Table
          headers={['Type', 'Txn No', 'Date', 'Customer', 'Script', 'Lots', 'Qty', 'Point Val', 'Price', 'Gross', 'Brokerage', 'Net Amount', 'P&L']}
          rows={txns.map(t => {
            const pl = parseFloat(t.profit_loss || 0);
            const hasPL = t.profit_loss !== null;
            const isBuy = t.transaction_type === 'BUY';
            return [
              <Badge color={isBuy ? 'green' : 'red'}>{t.transaction_type}</Badge>,
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>{t.transaction_no}</span>,
              t.trade_date?.split('T')[0],
              <div><div style={{ fontWeight: 600 }}>{t.customer_code}</div><div style={{ fontSize: 11, color: '#64748b' }}>{t.customer_name}</div></div>,
              t.script_name,
              <Badge color={isBuy ? 'blue' : 'orange'}>{parseFloat(t.total_lots || 0).toFixed(2)}</Badge>,
              t.quantity,
              <Badge color="purple">{t.point_value}</Badge>,
              fmt(t.price),
              fmt(t.gross_amount),
              fmt(t.brokerage_amount),
              <strong style={{ color: isBuy ? '#1d4ed8' : '#dc2626' }}>{fmt(t.net_amount)}</strong>,
              hasPL
                ? <strong style={{ color: pl >= 0 ? '#059669' : '#dc2626' }}>{fmt(pl)}</strong>
                : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>
            ];
          })}
          empty="No transactions found for the selected filters."
        />
      </Card>
    </div>
  );
}
